<template>
    <div class="listing-settings-header">
        <div v-if="filters.length > 0" class="listing-settings-header__filters-container">
            <CompassMenu
                trigger="click"
                class="listing-settings-header__filters-select"
                customClass="listing-settings-header__filters-select-dropdown"
            >
                <CompassButton
                    :label="$t.__('listing.filters.ListingTableFilters.filters')"
                    type="secondary"
                    leftIcon="plus"
                />

                <template slot="dropdown">
                    <CompassMenuItem
                        v-for="filter in filters"
                        :key="`listing-settings-header__filtres-${filter.code}`"
                        @click="onFilterSelected(filter)"
                    >
                        <span class="typographies-paragraph-medium-regular">
                            {{ filter.label }}
                        </span>
                    </CompassMenuItem>
                </template>
            </CompassMenu>

            <div v-show="hasActiveFilters" class="listing-settings-header__filters-tags">
                <slot name="filters" />
            </div>
        </div>

        <div class="listing-settings-header__utils-container">
            <span
                v-if="displayTotalResults"
                class="typographies-paragraph-medium-bold listing-settings-header__utils-total-results"
            >
                {{ textTotalResults }}
            </span>
            <CompassInput
                v-if="searchable"
                class="listing-settings-header__utils-search"
                :value="searchValue"
                :placeholder="$t.__('common.labels.search')"
                prefixIcon="search"
                @update:value="updateSearchValue"
            >
                <template slot="suffix">
                    <CompassIconButton
                        v-show="searchValue.length > 0"
                        class="listing-settings-header__utils-search-reset"
                        size="small"
                        icon="close"
                        @click="updateSearchValue('')"
                    />
                </template>
            </CompassInput>

            <CompassTooltip
                v-if="refreshable"
                :content="$t.__('common.actions.refresh')"
                placement="top-end"
            >
                <CompassIconButton type="secondary" icon="reload" @click="handleRefresh" />
            </CompassTooltip>

            <ListingSettings
                v-if="enableListingSettings"
                :enableColumnsSettings="enableColumnsSettings"
                :enablePaginationSettings="enablePaginationSettings"
                :searchableColumns="searchableColumns"
                @updateColumns="updateColumns"
                @updatePagination="updatePagination"
            />
        </div>
    </div>
</template>

<script>
import ListingSettings from 'vcomponents/common/listing/settings/ListingSettings.vue';

export default {
    components: {
        ListingSettings,
    },
    props: {
        displayTotalResults: {
            type: Boolean,
            default: false,
        },
        totalResults: {
            type: Number,
            default: 0,
        },
        filters: {
            type: Array,
            default: () => [],
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        enableColumnsSettings: {
            type: Boolean,
            default: false,
        },
        enablePaginationSettings: {
            type: Boolean,
            default: false,
        },
        searchableColumns: {
            type: Boolean,
            default: true,
        },
        searchValue: {
            type: String,
            default: '',
        },
        refreshable: {
            type: Boolean,
            default: false,
        },
        customTotalResultsLabel: {
            type: String,
            default: '',
        },
    },
    computed: {
        hasActiveFilters() {
            return this.filters.some((filter) => filter.isActive);
        },
        enableListingSettings() {
            return this.enableColumnsSettings || this.enablePaginationSettings;
        },
        textTotalResults() {
            if (this.customTotalResultsLabel) {
                return this.customTotalResultsLabel;
            }

            if (this.totalResults === 0) {
                return this.$t.__('listing.settings.ListingSettingsHeader.no_results');
            }

            return this.$t.n__(
                'listing.settings.ListingSettingsHeader.total_results',
                null,
                this.totalResults,
                this.totalResults,
            );
        },
    },
    methods: {
        onFilterSelected(filterSelected) {
            setTimeout(() => {
                this.$emit('selectFilter', filterSelected);
            }, 0);
        },
        updateColumns(newColumns) {
            this.$emit('updateColumns', newColumns);
        },
        updatePagination(newPagination) {
            this.$emit('updatePagination', newPagination);
        },
        updateSearchValue(newValue) {
            this.$emit('search', newValue);
        },
        handleRefresh() {
            this.$emit('refresh');
        },
    },
};
</script>

<style lang="scss" scoped>
.listing-settings-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--spacing-3);
    padding: var(--spacing-3);
    border-radius: var(--spacing-0) var(--spacing-0) 0px 0px;
    border: 1px solid var(--colors-common-border-default);
    border-bottom-width: 0;
    background-color: var(--colors-common-background-default);

    &__filters {
        &-container {
            display: flex;
            align-items: stretch;
            justify-content: flex-start;
            gap: var(--spacing-3);
        }

        &-select {
            flex-shrink: 0;
            position: relative;
            padding-right: var(--spacing-3);

            &:after {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                width: 1px;
                border-radius: 1px;
                background-color: var(--colors-common-border-lighter);
            }
        }

        &-tags {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-wrap: wrap;
            gap: var(--spacing-3);
        }
    }

    &__utils {
        &-container {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: var(--spacing-3);
            flex-shrink: 0;
            margin-left: auto;
        }

        &-search {
            min-width: 240px;
        }
        &-total-results {
            color: var(--colors-common-text-subtle-default);
        }
    }
}
</style>

<style lang="scss">
#compass-menu-container {
    .listing-settings-header__filters-select-dropdown {
        z-index: 3;
    }
}
</style>
